import React, { Component } from "react";
import { connect } from "react-redux";
import { Dropdown } from "react-bootstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { Content, Row, Col } from "../../components/GridComponent/AdminLte.js";
import RequestButtons from "../../components/RequestComponent/RequestButtons/RequestButtons";
import { getNhoTranslation, nhoLanguages } from "../../store/nhoTranslations.js";
import { addNhoSurvey } from "../../store/actions/userActions";

/**
 * The `nho_survey` custom-form slot: the New Hire Orientation survey body (SPEC 7.4 / 21.7).
 *
 * The questions, field names, layout classes (nho-grid / nho-cell, styled in Dashboard.css) and
 * the FR / NL translations are the hand-coded NHO modal's, moved out of Dashboard.js. The frame
 * owns the title and the mandatory chrome; this body owns the Submit button.
 *
 *   payload = { language: 'fr'|'en', can_translate: bool, hire_date: 'Y-m-d'|null }
 *
 * - the survey opens in payload.language (French for Morocco, decided server-side) and the
 *   language toggle is shown only when payload.can_translate is true
 * - nho_date is read-only: payload.hire_date shown as "MMMM D, YYYY" and posted as YYYY-MM-DD;
 *   with no hire date the field is empty and left out of the FormData (the server computes it)
 * - every field is required; Submit builds a multipart FormData of the survey's own fields and
 *   calls addNhoSurvey (POST /nho_survey/ — the thunk alerts and opens the Glassdoor link itself);
 *   true -> onComplete(), false -> the form stays with its answers
 * - one request at a time: a second Submit while the first is in flight is ignored
 * - the frame's title follows the language: onTitle(nhoT.title) on mount and on every switch
 *   (the hand-coded modal rendered {nhoT.title} in #nho-modal-title, Dashboard.js:635 at 37c3f67d)
 */

// [field name, translation key] in the order the form shows them; q1 (date) and q6 (facilitator)
// sit between the two halves.
const RATINGS_BEFORE_FACILITATOR = [
  ["onboarding_exp_rating", "q2"],
  ["recruitment_exp_rating", "q3"],
  ["schedule_awareness_rating", "q4"],
  ["topic_relevance_rating", "q5"],
];
const RATINGS_AFTER_FACILITATOR = [
  ["facilitator_knowledge_rating", "q7"],
  ["facilitator_presentation_rating", "q8"],
  ["facilitator_response_rating", "q9"],
  ["equipment_rating", "q10"],
  ["accessibility_rating", "q11"],
  ["welcome_rating", "q12"],
];
const RATING_FIELDS = RATINGS_BEFORE_FACILITATOR.concat(RATINGS_AFTER_FACILITATOR).map((pair) => pair[0]);

const DATE_DISPLAY = "MMMM D, YYYY";

// Same rules as the old validationSchemaNHO; the message follows the chosen language.
const validationSchemaNHO = (requiredMsg) => {
  const shape = {
    suggestions: Yup.string().required(requiredMsg).nullable(),
    nho_overall_feedback: Yup.string().required(requiredMsg).nullable(),
    facilitator_id: Yup.string().required(requiredMsg).nullable(),
  };
  RATING_FIELDS.forEach((name) => {
    shape[name] = Yup.string().required(requiredMsg).nullable();
  });
  return Yup.object().shape(shape);
};

class NhoSurveySlot extends Component {
  constructor(props) {
    super(props);
    const payload = props.payload || {};
    this.state = { nhoLang: payload.language || "en" };
    this.inFlight = false;
  }

  componentDidMount() {
    this.mounted = true;
    this.pushTitle();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.nhoLang !== this.state.nhoLang) this.pushTitle();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  pushTitle = () => {
    if (typeof this.props.onTitle === "function") this.props.onTitle(getNhoTranslation(this.state.nhoLang).title);
  };

  initialValues = () => {
    const hireDate = (this.props.payload || {}).hire_date;
    const values = {
      nho_date: hireDate ? moment(hireDate, "YYYY-MM-DD").format(DATE_DISPLAY) : "",
      facilitator_id: null,
      suggestions: null,
      nho_overall_feedback: null,
    };
    RATING_FIELDS.forEach((name) => {
      values[name] = null;
    });
    return values;
  };

  // Multipart body of the survey's own answers (the old form also carried the other pop-ups'
  // shared Formik values; the server never read them).
  toFormData = (values) => {
    const formData = new FormData();
    Object.keys(values).forEach((key) => {
      if (values[key] == null || key === "action") return;
      if (key === "nho_date") {
        if (values[key]) formData.append(key, moment(values[key], DATE_DISPLAY).format("YYYY-MM-DD"));
        return;
      }
      formData.set(key, values[key]);
    });
    return formData;
  };

  onSubmit = (values) => {
    if (this.inFlight) return;
    this.inFlight = true;
    Promise.resolve(this.props.addNhoSurvey(this.toFormData(values))).then((ok) => {
      this.inFlight = false;
      if (ok && this.mounted) this.props.onComplete();
    });
  };

  renderRating = (name, label, handleChange, cellClass) => (
    <div className={"nho-cell " + cellClass} key={name}>
      <div className="form-group">
        <label className="nho-required survey-label">{label}</label>
        <div className="nho-rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <React.Fragment key={n}>
              <input name={name} type="radio" value={String(n)} onChange={handleChange} />
              <label htmlFor={name}>&nbsp;{n}&nbsp;</label>
            </React.Fragment>
          ))}
        </div>
        <ErrorMessage component="div" name={name} className="input-feedback" />
      </div>
    </div>
  );

  renderLanguageToggle = (currentLabel) => (
    <div id="nho-lang-toggle" style={{ marginLeft: "auto", marginRight: 0 }}>
      <Dropdown>
        <Dropdown.Toggle
          size="sm"
          variant="outline-secondary"
          id="nho-lang-dropdown"
          style={{ backgroundColor: "#82af13", borderColor: "#82af13", color: "#ffffff" }}
        >
          <span>{currentLabel}</span>
          <i className="fa fa-chevron-down nho-lang-chevron" />
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {nhoLanguages.map((lang) => (
            <Dropdown.Item
              key={lang.code}
              active={this.state.nhoLang === lang.code}
              onClick={() => this.setState({ nhoLang: lang.code })}
            >
              {lang.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  render() {
    const { settings, payload } = this.props;
    const hr_list = (settings && settings.hr_list) || [];
    const nhoT = getNhoTranslation(this.state.nhoLang);
    const currentLang = nhoLanguages.find((lang) => lang.code === this.state.nhoLang);
    const currentLabel = currentLang ? currentLang.label : "English";
    // Alternate left / right cells as the old two-column grid did.
    let cell = 0;
    const nextCell = () => (cell++ % 2 === 0 ? "nho-cell-left" : "");

    return (
      <div className="nho-survey-body">
        {payload && payload.can_translate ? this.renderLanguageToggle(currentLabel) : null}
        <Formik
          enableReinitialize
          onSubmit={this.onSubmit}
          validationSchema={validationSchemaNHO(nhoT.required)}
          initialValues={this.initialValues()}
        >
          {({ values, handleSubmit, handleChange }) => (
            <form onSubmit={handleSubmit}>
              <Content col="12">
                <Row>
                  <Col size="12">
                    <div className="form-group survey-description">
                      <p>{nhoT.intro}</p>
                      <div className="nho-legend">
                        {nhoT.legend.map((line) => (<span key={line} className="nho-legend-item">{line}</span>))}
                      </div>
                      <p className="survey-note">{nhoT.note}</p>
                    </div>
                  </Col>
                </Row>
                <div className="nho-grid">
                  <div className={"nho-cell " + nextCell()}>
                    <div className="form-group">
                      <label className="nho-required survey-label">{nhoT.q1}</label>
                      <div className="nho-date-wrap">
                        <input type="text" name="nho_date" className="form-control" value={values.nho_date} disabled />
                        <i className="fa fa-calendar nho-date-icon" aria-hidden="true"></i>
                      </div>
                    </div>
                  </div>
                  {RATINGS_BEFORE_FACILITATOR.map(([name, key]) => this.renderRating(name, nhoT[key], handleChange, nextCell()))}
                  <div className={"nho-cell " + nextCell()}>
                    <div className="form-group">
                      <label className="nho-required survey-label">{nhoT.q6}</label>
                      <select className="form-control" name="facilitator_id" onChange={handleChange} value={values.facilitator_id || ""} style={{ display: "block" }}>
                        <option value="">{nhoT.selectFacilitator}</option>
                        {hr_list.map((hr) => (
                          <option key={hr.id} value={hr.id}>{hr.empname}</option>
                        ))}
                      </select>
                      <ErrorMessage component="div" name="facilitator_id" className="input-feedback" />
                    </div>
                  </div>
                  {RATINGS_AFTER_FACILITATOR.map(([name, key]) => this.renderRating(name, nhoT[key], handleChange, nextCell()))}
                  <div className="nho-cell nho-cell-full">
                    <div className="form-group">
                      <label className="nho-required survey-label">{nhoT.q13}</label>
                      <textarea className="form-control" rows="3" name="suggestions" onChange={handleChange} value={values.suggestions || ""}></textarea>
                      <ErrorMessage component="div" name="suggestions" className="input-feedback" />
                    </div>
                  </div>
                  <div className="nho-cell nho-cell-full">
                    <div className="form-group">
                      <label className="nho-required survey-label">{nhoT.q14}</label>
                      <textarea className="form-control" rows="3" name="nho_overall_feedback" onChange={handleChange} value={values.nho_overall_feedback || ""}></textarea>
                      <ErrorMessage component="div" name="nho_overall_feedback" className="input-feedback" />
                    </div>
                  </div>
                </div>
                <div className="nho-submit-row">
                  <RequestButtons method="store" noBackBtn={true} />
                </div>
              </Content>
            </form>
          )}
        </Formik>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    addNhoSurvey: (post_data) => dispatch(addNhoSurvey(post_data)),
  };
};

export default connect(null, mapDispatchToProps)(NhoSurveySlot);
